// Setup › Add digital employee (`/c/{slug}/setup/des/new`; DE-WIZARD, review #12): the one way
// to add a DE. It replaces "New digital employee" (KPIs, no standing orders) and "Deploy an
// agent" (standing orders, budget and a channel, no KPIs); `/setup/agents/deploy` redirects
// here. Reached from Agents › roster "+ Add", Setup's sidebar and the chart (an open seat's
// "Fill with a DE", `?seat=`). Seven steps on the §6.9 Wizard template (model.ts `STEPS`):
// template (or blank) → name & seat (new or open; reports to; team) → job & standing orders
// (editable without a template) → KPIs (template defaults or the catalog, weekly targets, which
// it reports itself) → models & budget → channels & access (home channel, joins, approver of
// its posts) → review.
//
// The two paths keep spec 05's rules: "Deploy it yourself" (the self-serve catalog, the
// `agents` add-on, the company's owner or an admin; no approval) and "the operator sets it up"
// (done-for-you: any manager asks, the operator stages the first deploy and the owner approves it).
// TEMPLATES-T2: a role template (`?role=` from Workforce › Templates) is a done-for-you choice
// too: its plan prefills the KPIs, budget and tier, Channels & access adds the role's access
// step, and finishing applies the role (`applyRole`, its own idempotency key) right after the DE
// is made, then sets the KPI targets over the definitions it returns.
//
// Finishing runs the calls the server needs, in order, remembering what each made so a retry
// picks up where it stopped: the seat (if new) and its team, the DE (`seed_template_metrics:
// false`: the KPI step is what the seat gets), its job (`PUT …/job`: path, KPIs and targets,
// reporting, home channel, post approval, submitted), its standing orders and models (the
// template-less override, `template_id: null`, which any later deploy builds with), the seat's
// budget, the channel joins, and on the self-serve path the deploy. "Save as draft" stops before
// submitting: the DE reads Draft until its set-up is finished from the profile (Edit).
import { profileGaps } from "@companyos/schema/contracts";
import { ApiError, newIdempotencyKey } from "@companyos/sdk";
import { Button, buttonVariants, cn, Notice, SettingsSection, Wizard } from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClipboardCheckIcon, RocketIcon, SendIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { Api } from "../../../api/client";
import type { Deploy } from "../../../api/types";
import { useCurrency } from "../../agents/agent-parts";
import { budgetsQuery, invalidateAgents } from "../../agents/agent-queries";
import { orgChartQuery } from "../../chart/chart-queries";
import { boardsQuery } from "../../chat/chat-queries";
import type { CompanyAccess } from "../../company";
import { useReachConfirm } from "../../memory/reach-confirm";
import { problemLines } from "../../operator/templates/template-parts";
import { useOperatorName } from "../../operator-name";
import { deQuery, keys, metricsQuery, rolesQuery } from "../../queries";
import { Facts, LoadError, Loading } from "../../screen-parts";
import { SetupPage } from "../../settings/setup-page";
import {
  accessChanges,
  FAMILY_LABELS,
  forcedLater,
  LEVEL_LABELS,
  majorOf,
  overlaysQuery,
  packOverlaysOf,
  rolePlanQuery,
  roleTemplatesQuery,
  TIER_LABELS,
  templateKeys,
  unconfirmedWidening,
  unresolvedConnections,
  widens,
} from "../../templates/role-model";
import { useFeature } from "../../use-feature";
import { DeLiveBadge, liveness } from "../liveness";
import { type ReadinessState, ReadinessStrip } from "../readiness";
import { UnverifiedBadge } from "../unverified-badge";
import { useJoinableChannels } from "./channels";
import {
  choicesOf,
  currentMonth,
  DUTY_LABELS,
  emptyForm,
  type Form,
  jobKey,
  mapOfRows,
  mayStage,
  NEW_SEAT,
  type Path,
  roleKpiRows,
  rowsOfMap,
  STEPS,
  selfServeTemplatesQuery,
  type TemplateChoice,
  targetValid,
  templateKpis,
} from "./model";
import { RoleAccessStep } from "./role-access-step";
import {
  ChannelsStep,
  type ChartSeat,
  hasPerson,
  JobStep,
  KpisStep,
  ModelsStep,
  SeatStep,
  seatLabel,
  TemplateStep,
} from "./steps";

/** What finishing has made so far (a retry skips these). */
type Progress = {
  seatId?: string;
  teamed?: boolean;
  deId?: string;
  /** TEMPLATES-T2: the role applied (its KPI definitions by metric key). */
  role?: { key: string; metric_def_id: string | null }[];
  job?: boolean;
  orders?: boolean;
  budget?: boolean;
  joined?: string[];
  refused?: string[];
  deploy?: Deploy;
  done?: "draft" | "submitted" | "deployed";
};

export function AddDeWizard({
  api,
  company,
  seat: presetSeat,
  role: presetRole,
}: {
  api: Api;
  company: CompanyAccess;
  /** `?seat=`: the chart's "Fill with a DE" (an open seat). */
  seat?: string | undefined;
  /** `?role=`: Templates' "Deploy" (a role template's id), picked once the roles load. */
  role?: string | undefined;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const operator = useOperatorName();
  const agentsOn = useFeature("agents", co).enabled;
  const chatOn = useFeature("chat", co).enabled;
  const kpiOn = useFeature("kpi", co).enabled;
  const staging = mayStage(company);
  const selfServeOk = agentsOn && staging;
  const catalog = useQuery({ ...selfServeTemplatesQuery(api, co), enabled: selfServeOk });
  const chart = useQuery(orgChartQuery(api, co));
  const roles = useInfiniteQuery(rolesQuery(api, co));
  const metrics = useInfiniteQuery({ ...metricsQuery(api, co), enabled: kpiOn });
  const budgets = useInfiniteQuery(budgetsQuery(api, co));
  const { joinable, list: channelList } = useJoinableChannels(api, co, chatOn);
  const currency = useCurrency(api, co) ?? "USD";
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Form>(() => ({ ...emptyForm(), seat: presetSeat ?? "" }));
  const [progress, setProgress] = useState<Progress>({});
  const [idem] = useState(() => ({
    seat: newIdempotencyKey(),
    de: newIdempotencyKey(),
    budget: newIdempotencyKey(),
    deploy: newIdempotencyKey(),
    role: newIdempotencyKey(),
  }));
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  // TEMPLATES-T2: the role templates are choices too (done for you, prefilled from the plan).
  const roleList = useQuery(roleTemplatesQuery(api, co));
  const choices = choicesOf(
    selfServeOk ? (catalog.data?.data ?? null) : null,
    roleList.data?.data ?? [],
    operator,
  );
  const choice = choices.find((c) => c.id === form.choice) ?? null;
  const roleId = choice?.roleTemplateId ?? null;
  const plan = useQuery({
    ...rolePlanQuery(api, co, roleId ?? "", { vertical: form.verticalOverlayId || null }),
    enabled: roleId !== null,
  });
  const overlays = useQuery({ ...overlaysQuery(api, co), enabled: roleId !== null });
  const packs =
    choice && roleId ? packOverlaysOf(overlays.data?.data ?? [], choice.templateKey) : [];
  const boards = useQuery({
    ...boardsQuery(api, co),
    enabled: roleId !== null && (plan.data?.cadence.length ?? 0) > 0,
  });
  const [applyError, setApplyError] = useState<unknown>(null);
  // The plan a role's prefill was made from (`{role}:{pack}`): a refetch never overwrites edits.
  const [prefilled, setPrefilled] = useState("");
  const selfServe = choice?.path === "self_serve";
  const seats: ChartSeat[] = chart.data?.seats ?? [];
  const seatById = new Map(seats.map((s) => [s.id, s]));
  const newSeat = form.seat === NEW_SEAT;
  const picked = newSeat ? undefined : seatById.get(form.seat);
  const supervisorId = newSeat ? form.supervisorSeatId : (picked?.supervisor_seat_id ?? "");
  const supervisor = seatById.get(supervisorId);
  const seatId = newSeat ? (progress.seatId ?? null) : (picked?.id ?? null);
  const seatBudget = (budgets.data?.pages ?? [])
    .flatMap((p) => p?.data ?? [])
    .find((b) => b.subject_kind === "seat" && seatId !== null && b.subject_id === seatId);
  const allMetrics = metrics.data?.pages.flatMap((p) => p.data) ?? [];

  // `?role=`: Templates' Deploy picks the role once the list is in (only while nothing is picked).
  const presetChoice = presetRole
    ? choices.find((c) => c.roleTemplateId === presetRole)
    : undefined;
  useEffect(() => {
    if (presetChoice && !form.choice) {
      setPrefilled("");
      setForm((f) => ({ ...f, choice: presetChoice.id }));
    }
  }, [presetChoice, form.choice]);

  // A role's plan prefills the KPIs (names and targets), the budget, and the access step.
  const planData = plan.data;
  useEffect(() => {
    if (!planData || !roleId) return;
    const tag = `${roleId}:${form.verticalOverlayId}`;
    if (prefilled === tag) return;
    setPrefilled(tag);
    setForm((f) => ({
      ...f,
      kpis: roleKpiRows(planData, allMetrics),
      amount: majorOf(planData.budget_minor),
      access: {},
      confirmWiden: [],
      connectLater: forcedLater(planData),
    }));
  }, [planData, roleId, form.verticalOverlayId, prefilled, allMetrics]);
  const templateText = choice?.version?.version.standing_orders_text ?? null;
  const templateMap = choice?.version?.version.role_model_map ?? null;
  const ordersOwn = selfServe ? form.orders !== templateText : form.orders.trim() !== "";
  const mapNow = mapOfRows(form.models);
  const modelsOwn = selfServe
    ? JSON.stringify(mapNow) !== JSON.stringify(mapOfRows(rowsOfMap(templateMap)))
    : Object.keys(mapNow).length > 0;
  const amountMinor = Math.round(Number.parseFloat(form.amount) * 100);
  const needsBudget = !seatBudget && !progress.budget;
  /** 05: standing orders and models are set by an admin or the owner (or the supervisor). */
  const writesOrders = staging;

  const stepValid = [
    choice !== null,
    Boolean(form.name.trim()) &&
      // CLOCKWORK B2: a DE defined here has a role, a goal and a backstory (a template DE is
      // given the platform default and edited on its Overview tab).
      (choice?.custom !== true || profileGaps(form).length === 0) &&
      (newSeat
        ? Boolean(form.seatTitle.trim() && form.roleId && hasPerson(supervisor))
        : Boolean(picked) && hasPerson(supervisor)),
    !selfServe || Boolean(form.orders.trim()),
    form.kpis.every((k) => targetValid(k.target)),
    (!selfServe || Object.keys(mapNow).length > 0) &&
      (!needsBudget || (Number.isFinite(amountMinor) && amountMinor > 0)),
    // TEMPLATES-T2: a role's required connections are connected or "connect later", and every
    // level above the role's default is confirmed.
    roleId === null ||
      (planData !== undefined &&
        unresolvedConnections(planData, form.connectLater).length === 0 &&
        unconfirmedWidening(planData, form.access, form.confirmWiden).length === 0),
    true,
  ];

  // D8: before the DE is created, what it will read in memory (its seat's departments)
  const { confirmReach, reachDialog } = useReachConfirm(api, co);
  const start = async (mode: "draft" | "go") => {
    if (
      !progress.deId &&
      !(await confirmReach({
        kind: "seat_holder",
        membership_id: null,
        add_seat_id: (newSeat ? progress.seatId : picked?.id) ?? null,
        end_seat_id: null,
        add_team_id: form.teamId || null,
      }))
    )
      return;
    finish.mutate(mode);
  };

  const pick = (c: TemplateChoice) => {
    // a role's KPIs, budget and access come from its plan (the prefill effect)
    setPrefilled("");
    set({
      choice: c.id,
      orders: c.version?.version.standing_orders_text ?? "",
      models: rowsOfMap(c.version?.version.role_model_map),
      kpis: c.roleTemplateId ? [] : templateKpis(c.templateKey, allMetrics),
      access: {},
      confirmWiden: [],
      connectLater: [],
      verticalOverlayId: "",
      routineBoardId: "",
    });
  };

  const finish = useMutation({
    mutationFn: async (mode: "draft" | "go") => {
      if (!choice) throw new Error("no template");
      let p = progress;
      const save = (patch: Progress) => {
        p = { ...p, ...patch };
        setProgress(p);
      };
      if (newSeat && !p.seatId) {
        const s = await api.client.seats.create({
          params: { co },
          idempotencyKey: idem.seat,
          body: {
            title: form.seatTitle.trim(),
            role_id: form.roleId,
            supervisor_seat_id: form.supervisorSeatId,
          },
        });
        save({ seatId: s.id });
      }
      const seat = newSeat ? p.seatId : picked?.id;
      if (!seat) throw new Error("no seat");
      if (form.teamId && !p.teamed) {
        const teamId = form.teamId;
        await api.client.teams
          .addMember({ params: { co, id: teamId }, body: { seat_id: seat } })
          .catch(async (e: unknown) => {
            // A 409 is a retry's success only when the seat is in the team already; any other
            // refusal (a sub-team the seat's parent does not hold) stops before the DE is made,
            // so it never ends with less memory than the D8 dialog showed.
            if (!(e instanceof ApiError && e.status === 409)) throw e;
            const t = await api.client.teams.get({ params: { co, id: teamId } });
            if (!t.members.some((m) => m.seat_id === seat)) throw e;
          });
        save({ teamed: true });
      }
      if (!p.deId) {
        const de = await api.client.digitalEmployees.create({
          params: { co },
          idempotencyKey: idem.de,
          body: {
            display_name: form.name.trim(),
            template_key: choice.templateKey,
            seat_id: seat,
            seed_template_metrics: false,
            // WF-POLISH: Custom's profile (blank fields are left out).
            ...(choice.custom
              ? {
                  role: form.role.trim(),
                  goal: form.goal.trim(),
                  backstory: form.backstory.trim(),
                }
              : {}),
          },
        });
        save({ deId: de.membership_id });
      }
      const deId = p.deId as string;
      // TEMPLATES-T2: set the DE up from its role before its job (the KPI definitions come back).
      if (roleId && !p.role) {
        if (!planData) throw new Error("The role is still loading. Try again in a moment.");
        const changes = accessChanges(planData, form.access);
        const confirm = planData.access
          .filter(
            (a) =>
              form.confirmWiden.includes(a.family) &&
              widens(form.access[a.family] ?? a.level, a.role),
          )
          .map((a) => a.family);
        const later = [
          ...new Set([
            ...forcedLater(planData),
            ...planData.required_connections
              .filter((c) => c.status !== "connected" && form.connectLater.includes(c.family))
              .map((c) => c.family),
          ]),
        ];
        const applied = await api.client.digitalEmployees
          .applyRole({
            params: { co, id: deId },
            idempotencyKey: idem.role,
            body: {
              template_version_id: planData.template_version_id,
              ...(form.verticalOverlayId ? { vertical_overlay_id: form.verticalOverlayId } : {}),
              ...(Object.keys(changes).length ? { access: changes } : {}),
              ...(confirm.length ? { confirm_widen: confirm } : {}),
              ...(later.length ? { connect_later: later } : {}),
              ...(form.routineBoardId ? { routine_board_id: form.routineBoardId } : {}),
            },
          })
          .catch((e: unknown) => {
            // A refusal (403 widen, 422 connections) is final for that body: the corrected
            // retry is a new request.
            if (e instanceof ApiError) {
              idem.role = newIdempotencyKey();
              setApplyError(e);
            }
            throw e;
          });
        setApplyError(null);
        save({ role: applied.kpis.map((k) => ({ key: k.key, metric_def_id: k.metric_def_id })) });
      }
      // A role KPI's definition is the applied role's; a catalog KPI keeps its own.
      const roleDefs = new Map((p.role ?? []).map((k) => [k.key, k.metric_def_id]));
      const kpiRows = form.kpis.flatMap((k) => {
        const id = k.key
          ? (roleDefs.get(k.key) ?? (k.metric_def_id.startsWith("role:") ? null : k.metric_def_id))
          : k.metric_def_id;
        return id ? [{ ...k, metric_def_id: id }] : [];
      });
      if (!p.job || mode === "go") {
        await api.client.digitalEmployees.putJob({
          params: { co, id: deId },
          body: {
            path: choice.path,
            home_channel_id: form.homeChannel || null,
            ...(kpiOn && kpiRows.length
              ? {
                  kpis: kpiRows.map((k) => ({
                    metric_def_id: k.metric_def_id,
                    weekly_target: k.target.trim() ? k.target.trim() : null,
                    reported_by_agent: k.reports,
                    ...(k.reports ? { duty: k.duty } : {}),
                  })),
                }
              : {}),
            post_approval: {
              required: form.postApproval,
              approver_seat_id:
                form.postApproval && form.approverSeatId ? form.approverSeatId : null,
            },
            // The self-serve path is submitted by its deploy (below, after the budget).
            ...(mode === "go" && !selfServe ? { submit: true as const } : {}),
          },
        });
        save({ job: true });
      }
      if (writesOrders && !p.orders && (ordersOwn || modelsOwn)) {
        await api.client.digitalEmployees.putTemplateOverride({
          params: { co, id: deId },
          body: {
            template_id: null,
            ...(ordersOwn && form.orders.trim() ? { standing_orders_text: form.orders } : {}),
            ...(modelsOwn && Object.keys(mapNow).length ? { role_model_map: mapNow } : {}),
          },
        });
        save({ orders: true });
      }
      if (needsBudget && !p.budget) {
        await api.client.budgets.create({
          params: { co },
          idempotencyKey: idem.budget,
          body: {
            subject_kind: "seat",
            subject_id: seat,
            period_type: "month",
            period_key: currentMonth(),
            amount_minor: amountMinor,
            currency: currency as "USD",
          },
        });
        save({ budget: true });
      }
      if (chatOn && !p.joined) {
        const want = [...new Set([form.homeChannel, ...form.joins].filter(Boolean))];
        const joined: string[] = [];
        const refused: string[] = [];
        for (const id of want)
          await api.client.channels
            .addMember({ params: { co, id }, body: { membership_id: deId } })
            .then(() => joined.push(id))
            .catch((e: unknown) =>
              e instanceof ApiError && e.status === 409 ? joined.push(id) : refused.push(id),
            );
        save({ joined, refused });
      }
      if (mode === "draft") {
        save({ done: "draft" });
        return;
      }
      if (selfServe && choice.version) {
        const d = await api.client.agents
          .deploySelfServe({
            params: { co },
            idempotencyKey: idem.deploy,
            body: { de_membership_id: deId, template_version_id: choice.version.version.id },
          })
          .catch((e: unknown) => {
            // A refusal is final for that body: the corrected retry is a new request.
            if (e instanceof ApiError) idem.deploy = newIdempotencyKey();
            throw e;
          });
        await api.client.digitalEmployees.putJob({
          params: { co, id: deId },
          body: { submit: true },
        });
        save({ deploy: d, done: "deployed" });
      } else save({ done: "submitted" });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(co) });
      void qc.invalidateQueries({ queryKey: keys.seats(co) });
      if (progress.deId) void qc.invalidateQueries({ queryKey: jobKey(co, progress.deId) });
      if (roleId) void qc.invalidateQueries({ queryKey: templateKeys.list(co) });
      void invalidateAgents(qc, co);
    },
  });

  const page = (body: ReactNode) => (
    <SetupPage title="Add digital employee" slot="add-de">
      {body}
    </SetupPage>
  );

  if (progress.done && progress.deId)
    return page(
      <Finished
        api={api}
        company={company}
        deId={progress.deId}
        name={form.name.trim()}
        done={progress.done}
        path={choice?.path}
        refused={(progress.refused ?? []).map(
          (id) => joinable.find((c) => c.id === id)?.label ?? "a channel",
        )}
      />,
    );

  const loading =
    chart.isPending ||
    roles.isPending ||
    budgets.isPending ||
    (selfServeOk && catalog.isPending) ||
    (presetRole !== undefined && roleList.isPending) ||
    (kpiOn && metrics.isPending);
  const failed = chart.isError
    ? { what: "Seats", error: chart.error }
    : selfServeOk && catalog.isError
      ? { what: "Agent templates", error: catalog.error }
      : null;
  const selfServeNote = !agentsOn
    ? `Deploying it yourself needs the Agents add-on; ${operator} can still set it up for you.`
    : !staging
      ? `Only the company's owner or an admin deploys a DE themselves; you can ask ${operator} to set it up.`
      : null;
  const last = step === STEPS.length - 1;

  const facts = [
    {
      label: "Template",
      value: (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {choice?.name}
          {choice?.unverified ? <UnverifiedBadge /> : null}
        </span>
      ),
    },
    {
      label: "Path",
      value: selfServe
        ? "You deploy it (no approval)"
        : `${operator} sets it up, the owner approves`,
    },
    { label: "Name", value: form.name.trim() },
    {
      label: "Seat",
      value: newSeat ? `${form.seatTitle.trim()} (new)` : picked ? seatLabel(picked) : "—",
    },
    { label: "Reports to", value: supervisor ? seatLabel(supervisor) : "—" },
    {
      label: "Standing orders",
      value: !form.orders.trim()
        ? `The template's (${operator})`
        : ordersOwn
          ? "Your own"
          : "The template's",
    },
    {
      label: "KPIs",
      value: form.kpis.length
        ? form.kpis
            .map(
              (k) =>
                `${k.name}${k.target.trim() ? ` ${k.target.trim()}/wk` : ""}${k.reports ? ` (reports ${DUTY_LABELS[k.duty].toLowerCase()})` : ""}`,
            )
            .join(" · ")
        : "None",
    },
    {
      label: "Models",
      value: Object.keys(mapNow).length
        ? Object.entries(mapNow)
            .map(([r, e]) => `${r}: ${e.model}`)
            .join(", ")
        : `The template's (${operator})`,
    },
    {
      label: "Budget",
      value: seatBudget ? "The seat's own" : `${form.amount} ${currency} for ${currentMonth()}`,
    },
    ...(channelList
      ? [
          {
            label: "Channels",
            value:
              [form.homeChannel, ...form.joins]
                .filter(Boolean)
                .map((id) => joinable.find((c) => c.id === id)?.label ?? "#?")
                .join(", ") || "None",
          },
        ]
      : []),
    ...(roleId && planData
      ? [
          { label: "Model tier", value: TIER_LABELS[planData.model_tier] },
          {
            label: "Vertical pack",
            value: packs.find((o) => o.id === form.verticalOverlayId)?.name ?? "None",
          },
          {
            label: "Access",
            value:
              Object.entries(accessChanges(planData, form.access))
                .map(
                  ([f, l]) =>
                    `${FAMILY_LABELS[f as keyof typeof FAMILY_LABELS]}: ${LEVEL_LABELS[l]}`,
                )
                .join(", ") || "The role's",
          },
          {
            label: "Connect later",
            value:
              planData.required_connections
                .filter((c) => c.status !== "connected")
                .map((c) => FAMILY_LABELS[c.family])
                .join(", ") || "None",
          },
          {
            label: "Routines",
            value: form.routineBoardId
              ? `${planData.cadence.length} on ${boards.data?.data.find((b) => b.id === form.routineBoardId)?.name ?? "a board"}`
              : "None",
          },
        ]
      : []),
    {
      label: "Its posts",
      value: form.postApproval
        ? `Wait for ${form.approverSeatId ? (seatById.get(form.approverSeatId)?.title ?? "the approver") : "its supervisor"}`
        : "Posted directly",
    },
  ];

  return page(
    loading ? (
      <Loading label="Loading templates and seats" rows={3} />
    ) : failed ? (
      <LoadError what={failed.what} error={failed.error} />
    ) : (
      <form
        aria-label="Add digital employee"
        data-slot="add-de-wizard"
        data-step={STEPS[step]?.key}
        onSubmit={(e) => {
          e.preventDefault();
          if (!last) {
            if (stepValid[step]) setStep(step + 1);
          } else void start("go");
        }}
      >
        {reachDialog}
        <Wizard
          aria-label="Add steps"
          steps={STEPS.map((s) => ({ label: s.label, hint: s.hint }))}
          current={step}
          footer={
            <>
              {step > 0 ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  disabled={finish.isPending}
                  onClick={() => setStep(step - 1)}
                >
                  Back
                </Button>
              ) : null}
              {last ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    disabled={finish.isPending}
                    onClick={() => void start("draft")}
                  >
                    Save as draft
                  </Button>
                  <Button type="submit" size="lg" disabled={finish.isPending}>
                    {selfServe ? (
                      <RocketIcon aria-hidden="true" />
                    ) : (
                      <SendIcon aria-hidden="true" />
                    )}
                    {finish.isPending
                      ? "Working…"
                      : finish.isError
                        ? "Try again"
                        : selfServe
                          ? "Deploy"
                          : `Submit to ${operator}`}
                  </Button>
                </>
              ) : (
                <Button type="submit" size="lg" disabled={!stepValid[step]}>
                  Continue
                </Button>
              )}
            </>
          }
        >
          {step === 0 ? (
            <TemplateStep
              choices={choices}
              value={form.choice}
              onPick={pick}
              note={selfServeNote}
            />
          ) : step === 1 ? (
            <SeatStep
              form={form}
              set={set}
              seats={seats}
              roles={roles.data?.pages.flatMap((p) => p.data) ?? []}
              teams={chart.data?.teams ?? []}
              locked={Boolean(progress.deId)}
              custom={choice?.custom === true}
            />
          ) : step === 2 ? (
            <>
              <JobStep
                value={form.orders}
                onChange={(orders) => set({ orders })}
                templateText={templateText}
                required={selfServe}
                readOnly={!writesOrders}
              />
              {!writesOrders ? (
                <Notice tone="neutral" className="mt-3" data-slot="orders-owner-only">
                  Standing orders are set by the company's owner, an admin or the DE's supervisor.
                </Notice>
              ) : null}
            </>
          ) : step === 3 ? (
            <KpisStep
              kpiOn={kpiOn}
              rows={form.kpis}
              onChange={(kpis) => set({ kpis })}
              metrics={allMetrics}
            />
          ) : step === 4 ? (
            <>
              {roleId && planData ? (
                <p className="mb-3 text-sm text-muted-foreground" data-slot="role-tier">
                  Model tier:{" "}
                  <span className="text-foreground">{TIER_LABELS[planData.model_tier]}</span>
                </p>
              ) : null}
              <ModelsStep
                models={{
                  rows: form.models,
                  onChange: (models) => set({ models }),
                  required: selfServe,
                }}
                budget={{
                  amount: form.amount,
                  onChange: (amount) => set({ amount }),
                  currency,
                  month: currentMonth(),
                  existing: seatBudget
                    ? seatBudget.period_type
                    : progress.budget
                      ? "monthly"
                      : null,
                }}
              />
            </>
          ) : step === 5 ? (
            <>
              <ChannelsStep
                channels={channelList}
                home={form.homeChannel}
                onHome={(homeChannel) => set({ homeChannel })}
                joins={form.joins}
                onJoins={(joins) => set({ joins })}
                postApproval={form.postApproval}
                onPostApproval={(postApproval) => set({ postApproval })}
                approver={form.approverSeatId}
                onApprover={(approverSeatId) => set({ approverSeatId })}
                approverSeats={seats.filter((s) => s.status !== "closed" && hasPerson(s))}
                supervisorLabel={supervisor ? seatLabel(supervisor) : "its supervisor seat"}
              />
              {roleId ? (
                <div className="mt-4">
                  <RoleAccessStep
                    plan={planData}
                    pending={plan.isPending}
                    error={plan.error}
                    form={form}
                    set={set}
                    packs={packs}
                    boards={boards.data?.data ?? null}
                    applyError={applyError}
                  />
                </div>
              ) : null}
            </>
          ) : (
            <SettingsSection icon={ClipboardCheckIcon} title="Review">
              <Facts items={facts} />
              <Notice tone="neutral" data-slot="add-de-path">
                {selfServe
                  ? `Deploy builds ${form.name.trim() || "it"} from the template now and hands it to AiGentPanel; no approval is needed. It goes live once its model key is delivered.`
                  : `Submit asks ${operator} to set ${form.name.trim() || "it"} up: ${operator} stages the first deploy and the company's owner approves it in Setup › Deploys.`}{" "}
                Save as draft keeps everything without submitting.
              </Notice>
              {progress.deId && !progress.done ? (
                <Notice tone="neutral" data-slot="add-de-resume">
                  {form.name.trim()} is created; finishing again picks up from there.
                </Notice>
              ) : null}
              {finish.isError ? (
                <Notice tone="danger" role="alert" title="Not finished">
                  {problemLines(finish.error, "Check the steps and try again.").join(" · ")}
                </Notice>
              ) : null}
            </SettingsSection>
          )}
        </Wizard>
      </form>
    ),
  );
}

/**
 * The finish page's title. WF-FIX (Codex r3): "is live" only with a recent run or MCP call
 * (`liveness`); a DE the server calls live with no activity yet is "active, waiting for its
 * first run", as its profile says Idle.
 */
export function finishedTitle(
  name: string,
  done: "draft" | "submitted" | "deployed",
  readiness: ReadinessState | undefined,
  live: "live" | "idle" | undefined,
  operator = "Operator",
): string {
  if (done === "draft") return `${name} is saved as a draft`;
  if (done === "submitted") return `${name} is submitted to ${operator}`;
  if (readiness === "live")
    return live === "live" ? `${name} is live` : `${name} is active, waiting for its first run`;
  return readiness === "not_live" ? `${name} is deployed, not live yet` : `${name} is deploying`;
}

/** After finishing: the DE's readiness (followed while it deploys) and where to go next. */
function Finished({
  api,
  company,
  deId,
  name,
  done,
  path,
  refused,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  name: string;
  done: "draft" | "submitted" | "deployed";
  path: Path | undefined;
  /** The channels it could not join, labelled. */
  refused: string[];
}) {
  const operator = useOperatorName();
  const de = useQuery({
    ...deQuery(api, company.id, deId),
    refetchInterval: (q) =>
      q.state.data?.readiness.state === "deploying" || done === "deployed" ? 2000 : false,
  });
  const r = de.data?.readiness;
  // WF-FIX (Codex r3): live is earned by a run or MCP call, as on the profile ("Idle" before).
  const live = de.data ? liveness(de.data) : null;
  return (
    <SettingsSection
      icon={done === "deployed" ? RocketIcon : done === "submitted" ? SendIcon : ClipboardCheckIcon}
      title={finishedTitle(name, done, r?.state, live?.state, operator)}
      actions={de.data ? <DeLiveBadge de={de.data} /> : null}
    >
      <div data-slot="add-de-finished" data-done={done}>
        {r ? (
          <ReadinessStrip readiness={r} path={path} liveLabel={live?.label} />
        ) : (
          <Loading label="Loading readiness" rows={1} />
        )}
      </div>
      {refused.length ? (
        <Notice tone="warning" data-slot="add-de-join-refused">
          {name} could not join {refused.join(", ")}; add it from the channel's members in Chat.
        </Notice>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Link
          to="/c/$slug/agents/de/$id"
          params={{ slug: company.slug, id: deId }}
          search={{ tab: "overview" }}
          className={cn(buttonVariants())}
        >
          Open {name}
        </Link>
        <Link
          to="/c/$slug/agents/de/$id"
          params={{ slug: company.slug, id: deId }}
          search={{ tab: done === "draft" ? "deploy" : "job" }}
          className={cn(buttonVariants({ variant: "secondary" }))}
        >
          {done === "draft" ? "Finish set-up" : "Edit set-up"}
        </Link>
      </div>
    </SettingsSection>
  );
}
